import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    const access = localStorage.getItem('access_token');
    if (!access) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const response = await axios.get(`${API_URL}/auth/me/`, {
        headers: { Authorization: `Bearer ${access}` },
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        const refreshed = await refreshToken();
        if (!refreshed) {
          setUser(null);
        }
      } else {
        setUser(null);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshToken = async () => {
    const refresh = localStorage.getItem('refresh_token');
    if (!refresh) return false;
    try {
      const response = await axios.post(`${API_URL}/auth/refresh/`, { refresh });
      localStorage.setItem('access_token', response.data.access);
      if (response.data.refresh) {
        localStorage.setItem('refresh_token', response.data.refresh);
      }
      const userResponse = await axios.get(`${API_URL}/auth/me/`, {
        headers: { Authorization: `Bearer ${response.data.access}` },
      });
      setUser(userResponse.data);
      return true;
    } catch {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
      return false;
    }
  };

  const login = async (username, password) => {
    const response = await axios.post(`${API_URL}/auth/login/`, { username, password });
    localStorage.setItem('access_token', response.data.access);
    localStorage.setItem('refresh_token', response.data.refresh);
    const userData = await fetchUser();
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API_URL}/auth/register-org/`, data);
    localStorage.setItem('access_token', response.data.access);
    localStorage.setItem('refresh_token', response.data.refresh);
    await fetchUser();
    return response.data;
  };

  const registerWithInvite = async (token, data) => {
    const response = await axios.post(`${API_URL}/auth/register/invite/${token}/`, data);
    localStorage.setItem('access_token', response.data.access);
    localStorage.setItem('refresh_token', response.data.refresh);
    await fetchUser();
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('current_restaurant_id');
    setUser(null);
  };

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Role helpers
  const isOrgAdmin = user?.role === 'org_admin';
  const isRestaurantAdmin = user?.role === 'restaurant_admin';
  const isStaff = user?.role === 'staff';
  const isAdmin = isOrgAdmin || isRestaurantAdmin;
  const isSuperadmin = user?.is_superadmin === true;

  const hasModuleAccess = (moduleKey, requiredLevel = 'view') => {
    if (!user) return false;
    if (isSuperadmin || isOrgAdmin || isRestaurantAdmin) return true;
    const level = user.module_permissions?.[moduleKey] || 'none';
    if (requiredLevel === 'view') return level === 'view' || level === 'edit';
    if (requiredLevel === 'edit') return level === 'edit';
    return false;
  };

  return (
    <AuthContext.Provider value={{
      user, isAdmin, isOrgAdmin, isRestaurantAdmin, isStaff, isSuperadmin,
      loading, login, register, registerWithInvite, logout, fetchUser, hasModuleAccess,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
